export default class DateUtil {
  private static readonly EST_TIMEZONE = 'America/New_York'

  static getDateString(date: Date | string, utc: boolean = false): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: utc ? 'UTC' : undefined,
    })
  }

  static getRelativeTimeString(date: Date | string, utc: boolean = false): string {
    const now = utc ? new Date(new Date().toISOString()) : new Date()
    const inputDate = utc ? new Date(new Date(date).toISOString()) : new Date(date)
    let diffInSeconds = Math.floor((now.getTime() - inputDate.getTime()) / 1000)
    const isFuture = diffInSeconds < 0
    diffInSeconds = Math.abs(diffInSeconds)

    if (diffInSeconds < 60) {
      return 'just now'
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return isFuture
        ? `in ${minutes} minute${minutes !== 1 ? 's' : ''}`
        : `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return isFuture
        ? `in ${hours} hour${hours !== 1 ? 's' : ''}`
        : `${hours} hour${hours !== 1 ? 's' : ''} ago`
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400)
      return isFuture
        ? `in ${days} day${days !== 1 ? 's' : ''}`
        : `${days} day${days !== 1 ? 's' : ''} ago`
    } else {
      return this.getDateString(date, utc)
    }
  }

  /**
   * Interpret a date as a calendar date in America/New_York and return that day at 12:01 AM
   * in that zone (EDT or EST as appropriate). Date-only strings keep their YYYY-MM-DD parts.
   */
  static setTo1201AM(date: Date | string): Date {
    let year: number
    let month: number
    let day: number

    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      const [y, m, d] = date.slice(0, 10).split('-').map(Number)
      year = y
      month = m - 1
      day = d
    } else {
      const parts = this.getZonedParts(new Date(date), this.EST_TIMEZONE)
      year = parts.year
      month = parts.month - 1
      day = parts.day
    }

    return this.fromZonedTime(year, month, day, 0, 1, 0, this.EST_TIMEZONE)
  }

  static formatChallengeDateRange(start: Date, end: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      timeZone: this.EST_TIMEZONE,
    }
    const startLabel = start.toLocaleDateString('en-US', options)
    const endLabel = end.toLocaleDateString('en-US', options)
    return `${startLabel}–${endLabel}`
  }

  static formatStartsIn(target: Date, nowMs: number = Date.now()): string | null {
    const remainingMs = target.getTime() - nowMs
    if (remainingMs <= 0) return null

    const totalMinutes = Math.floor(remainingMs / 60_000)
    const days = Math.floor(totalMinutes / (24 * 60))
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60)
    const minutes = totalMinutes % 60

    const part = (value: number, unit: string) =>
      `${value} ${unit}${value === 1 ? '' : 's'}`

    if (days > 0) {
      return hours > 0
        ? `Starts in ${part(days, 'day')}, ${part(hours, 'hour')}`
        : `Starts in ${part(days, 'day')}`
    }
    if (hours > 0) {
      return minutes > 0
        ? `Starts in ${part(hours, 'hour')}, ${part(minutes, 'minute')}`
        : `Starts in ${part(hours, 'hour')}`
    }
    return `Starts in ${part(Math.max(1, minutes), 'minute')}`
  }

  /** Current instant. Challenge dates are real UTC instants for Eastern wall times. */
  static getNowInEST(): Date {
    return new Date()
  }

  static easternDaysUntil(from: Date, to: Date): number {
    return this.easternDayNumber(to) - this.easternDayNumber(from)
  }

  private static easternDayNumber(date: Date): number {
    const parts = this.getZonedParts(date, this.EST_TIMEZONE)
    return Date.UTC(parts.year, parts.month - 1, parts.day) / 86_400_000
  }

  private static getZonedParts(date: Date, timeZone: string) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
    const parts = formatter.formatToParts(date)
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '0'
    return {
      year: parseInt(get('year'), 10),
      month: parseInt(get('month'), 10),
      day: parseInt(get('day'), 10),
      hour: parseInt(get('hour'), 10),
      minute: parseInt(get('minute'), 10),
      second: parseInt(get('second'), 10),
    }
  }

  /** UTC instant for a wall-clock time in `timeZone` (handles EDT/EST). */
  private static fromZonedTime(
    year: number,
    monthIndex: number,
    day: number,
    hour: number,
    minute: number,
    second: number,
    timeZone: string,
  ): Date {
    let instant = Date.UTC(year, monthIndex, day, hour, minute, second)
    for (let i = 0; i < 3; i++) {
      const parts = this.getZonedParts(new Date(instant), timeZone)
      const asUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second,
      )
      const desired = Date.UTC(year, monthIndex, day, hour, minute, second)
      instant += desired - asUtc
    }
    return new Date(instant)
  }
}
